import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { money, padNum } from "@/lib/format";
import { LOGO_HORIZONTAL } from "@/lib/brand";
import { ticketUrl } from "@/lib/share";
import { QRCodeCanvas } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, Clock, XCircle, Trophy, Loader2, FileDown, ImageDown, Copy } from "lucide-react";

const STATUS = {
  approved: { label: "Aprobado", cls: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
  pending: { label: "En revisión", cls: "bg-amber-100 text-amber-700", Icon: Clock },
  reserved: { label: "Reservado", cls: "bg-amber-100 text-amber-700", Icon: Clock },
  rejected: { label: "Anulado", cls: "bg-red-100 text-red-700", Icon: XCircle },
  expired: { label: "Expirado", cls: "bg-neutral-200 text-neutral-600", Icon: XCircle },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("es-CR", { day: "2-digit", month: "long", year: "numeric" }) +
      " · " + d.toLocaleTimeString("es-CR", { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

export default function TicketView() {
  const { orderId } = useParams();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(null);
  const cardRef = useRef(null);

  useEffect(() => {
    (async () => {
      try { const res = await api.get(`/public/tickets/${orderId}`); setTicket(res.data); }
      catch (e) { setError(true); }
      finally { setLoading(false); }
    })();
  }, [orderId]);

  const capture = async () => {
    const html2canvas = (await import("html2canvas")).default;
    return html2canvas(cardRef.current, { scale: 2, backgroundColor: "#ffffff", useCORS: true });
  };

  const fileBase = `ticket-${(ticket?.buyer_name || "comprador").replace(/[^\w]+/g, "-").toLowerCase()}`;

  const saveImage = async () => {
    setBusy("img");
    try {
      const canvas = await capture();
      const a = document.createElement("a");
      a.href = canvas.toDataURL("image/png");
      a.download = `${fileBase}.png`;
      a.click();
      toast.success("Imagen guardada");
    } catch (e) {
      toast.error("No se pudo generar la imagen");
    } finally { setBusy(null); }
  };

  const savePdf = async () => {
    setBusy("pdf");
    try {
      const { jsPDF } = await import("jspdf");
      const canvas = await capture();
      const w = canvas.width / 2, h = canvas.height / 2;
      const pdf = new jsPDF({ unit: "pt", format: [w, h], orientation: w > h ? "landscape" : "portrait" });
      pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, 0, w, h);
      pdf.save(`${fileBase}.pdf`);
      toast.success("PDF descargado");
    } catch (e) {
      toast.error("No se pudo generar el PDF");
    } finally { setBusy(null); }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(ticketUrl(orderId));
      toast.success("Enlace copiado");
    } catch (e) { toast.error("No se pudo copiar el enlace"); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-neutral-50"><Loader2 className="animate-spin text-[#00AEEF]" /></div>;
  if (error || !ticket) return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 p-6">
      <div data-testid="ticket-not-found" className="bg-white rounded-2xl border border-neutral-200 p-8 text-center max-w-sm">
        <XCircle className="mx-auto text-red-500 mb-3" size={40} />
        <h1 className="font-bold text-lg">Ticket no encontrado</h1>
        <p className="text-neutral-500 text-sm mt-1">El enlace del ticket no es válido.</p>
      </div>
    </div>
  );

  const st = STATUS[ticket.status] || STATUS.pending;
  const StIcon = st.Icon;

  return (
    <div className="min-h-screen bg-neutral-50 py-8 px-4">
      <div className="max-w-md mx-auto">
        <div className="flex items-center justify-center gap-2 mb-6">
          <img src={LOGO_HORIZONTAL} alt="MCTECH EVENTOS" className="h-7 w-auto" />
          <span className="font-display font-extrabold tracking-tight">EVENTOS</span>
        </div>
        <div ref={cardRef} data-testid="ticket-card" className="bg-white rounded-2xl border border-neutral-200 overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.06)]">
          {ticket.raffle_image && (
            <div className="h-40 bg-neutral-100"><img src={ticket.raffle_image} alt={ticket.raffle_title} className="w-full h-full object-cover" /></div>
          )}
          <div className="p-6">
            <div className="flex items-center justify-between gap-2">
              <h1 className="font-display font-extrabold text-xl" data-testid="ticket-raffle-title">{ticket.raffle_title}</h1>
              <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${st.cls}`} data-testid="ticket-status">
                <StIcon size={13} /> {st.label}
              </span>
            </div>
            <p className="text-sm text-neutral-500 mt-1">Ticket de <b className="text-neutral-700" data-testid="ticket-buyer">{ticket.buyer_name}</b></p>
            <p className="text-xs text-neutral-400 mt-0.5" data-testid="ticket-date">Compra: {fmtDate(ticket.created_at)}</p>

            {ticket.won && (
              <div className="mt-4 flex items-center gap-2 rounded-xl bg-amber-50 border border-amber-200 p-3 text-amber-700" data-testid="ticket-winner">
                <Trophy size={18} /> <span className="font-semibold text-sm">¡Este ticket tiene número ganador! 🎉</span>
              </div>
            )}

            <div className="mt-4">
              <p className="text-xs uppercase tracking-wider text-neutral-400 font-semibold mb-2">Números ({ticket.numbers.length})</p>
              <div className="flex flex-wrap gap-1.5" data-testid="ticket-numbers">
                {[...ticket.numbers].sort((a, b) => a - b).map((n) => {
                  const isWin = (ticket.winning_numbers || []).includes(n);
                  return <span key={n} className={`text-sm font-bold px-2.5 py-1 rounded-lg ${isWin ? "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-500" : "bg-neutral-100 text-neutral-700"}`}>#{padNum(n)}</span>;
                })}
              </div>
            </div>

            <div className="mt-6 flex flex-col items-center rounded-xl bg-neutral-50 border border-neutral-200 p-5" data-testid="ticket-qr">
              <QRCodeCanvas value={ticketUrl(ticket.order_id)} size={168} bgColor="#fafafa" />
              <p className="text-xs text-neutral-500 mt-3 text-center">Presenta este código QR para validar tus números</p>
            </div>

            <p className="text-xs text-neutral-400 mt-4 text-center">Monto: {money(ticket.amount, ticket.currency)}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 mt-4">
          <Button data-testid="ticket-pdf-btn" onClick={savePdf} disabled={!!busy} variant="outline" className="rounded-xl gap-2 h-11">
            {busy === "pdf" ? <Loader2 size={16} className="animate-spin" /> : <FileDown size={16} />} PDF
          </Button>
          <Button data-testid="ticket-image-btn" onClick={saveImage} disabled={!!busy} variant="outline" className="rounded-xl gap-2 h-11">
            {busy === "img" ? <Loader2 size={16} className="animate-spin" /> : <ImageDown size={16} />} Imagen
          </Button>
        </div>
        <Button data-testid="ticket-copy-link-btn" onClick={copyLink} variant="ghost" className="w-full mt-2 rounded-xl gap-2 text-neutral-500 h-10">
          <Copy size={15} /> Copiar enlace para compartir
        </Button>
      </div>
    </div>
  );
}
